import { useEffect, useState } from 'react';

import Script from 'next/script';

import { getWindow } from '@r-client/shared/util/core';

type IZendeskScriptProps = {
  email?: string | null | undefined;
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  zendeskKey: string | undefined;
};

const settings = {
  webWidget: {
    zIndex: 101,
    offset: {
      horizontal: '55px',
      vertical: '2px',
      mobile: {
        horizontal: '-4px',
        vertical: '#{mobile_offset}',
      },
    },
    contactForm: {
      title: { '*': 'How can we help?' },
      ticketForms: [{ id: 6139814921239 }],
      fields: [
        { id: 'subject', prefill: { '*': 'Need help!' } },
        { id: 'email', prefill: { '*': '' } },
        {
          id: 'key:6164328823703',
          prefill: { '*': '' },
        },
      ],
    },
    helpCenter: {
      title: { '*': 'FAQ' },
      searchPlaceholder: { '*': 'What can we help you with?' },
      messageButton: { '*': 'Contact Us' },
      originalArticleButton: false,
    },
  },
};

const isLegacyZendeskWidget = () => {
  // TODO: https://linear.app/republic/issue/USMKT-1176/remove-support-for-legacy-zd-widget-once-we-fully-switch-to-the-new

  try {
    getWindow<{ zE: any }>()?.zE?.('webWidget', 'updateSettings', {});
    // Temporary: this console.log can be removed once we fully transition to the new Zendesk widget.
    // eslint-disable-next-line no-console
    console.log('Legacy Zendesk widget detected');
    return true;
  } catch {
    // Temporary: this console.log can be removed once we fully transition to the new Zendesk widget.
    // eslint-disable-next-line no-console
    console.log('New Zendesk widget detected');
    return false;
  }
};

const prefillContactForm = (
  email: string | null | undefined,
  firstName: string | null | undefined,
  lastName: string | null | undefined
) => {
  settings.webWidget.contactForm.fields = [
    { id: 'subject', prefill: { '*': 'Need help!' } },
    { id: 'email', prefill: { '*': email ?? '' } },
    {
      id: 'key:6164328823703',
      prefill: {
        '*': firstName && lastName ? `${firstName} ${lastName}` : '',
      },
    },
  ];
};

export const ZendeskScript = ({
  email,
  firstName,
  lastName,
  zendeskKey,
}: IZendeskScriptProps) => {
  // The script loads after hydration, so the prefill waits for it instead of reading `zE` on render.
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const zE = getWindow<{ zE: any }>()?.zE;
    if (!isReady || !zE || !isLegacyZendeskWidget()) return;
    prefillContactForm(email, firstName, lastName);
    zE('webWidget', 'updateSettings', settings);
  }, [email, firstName, lastName, isReady]);

  return (
    <Script
      id="ze-snippet"
      onReady={() => setIsReady(true)}
      src={`https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`}
      // Not `beforeInteractive`: that puts the snippet ahead of Next's chunks, so hydration waits for it.
      strategy="lazyOnload"
    />
  );
};
